import React, { useState, useEffect } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import IDCardGenerator, { IDCardPrintCart } from "../components/IDCardGenerator";
import UserInvites from "./UserInvites";
import ConnectedBots from "../Settings/ConnectedBots";
// Shared with the Invitations panel so the two lists cannot drift apart.
import { CAPABILITIES, ROLES } from "./userCapabilities";
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import ChipToggle from '../components/ui/ChipToggle';
import { Field, FieldRow } from '../components/ui/Field';
import SearchField from '../components/ui/SearchField';
import SegTrack from '../components/ui/SegTrack';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';

const UserAdmin = () => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("active");
  const [idCardUser, setIdCardUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [printCart, setPrintCart] = useState([]);
  const [showPrintCart, setShowPrintCart] = useState(false);

  // Edit form state
  const [editFirstname, setEditFirstname] = useState("");
  const [editLastname, setEditLastname] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editCapabilities, setEditCapabilities] = useState({});
  const [editPrimaryRole, setEditPrimaryRole] = useState("staff");

  const fetchUsers = async () => {
    try {
      const data = await api.get("/users");
      setUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const startEdit = (user) => {
    setEditingUser(user.id);
    setEditFirstname(user.firstname || "");
    setEditLastname(user.lastname || "");
    setEditEmail(user.email || "");
    setEditIsAdmin(user.isadmin || false);
    setEditCapabilities({
      can_admin: user.can_admin || false,
      can_operations: user.can_operations || false,
      can_manufacturing: user.can_manufacturing || false,
      can_producer: user.can_producer || false,
      can_sales: user.can_sales || false,
      can_ai: user.can_ai || false,
      can_analyst: user.can_analyst || false,
      can_pilot: user.can_pilot || false,
      can_manager: user.can_manager || false,
      can_procurement: user.can_procurement || false,
      can_swarm: user.can_swarm || false,
      can_financial: user.can_financial || false,
      can_rnd: user.can_rnd || false,
      can_connect_bot: user.can_connect_bot || false,
      can_animator: user.can_animator || false,
    });
    setEditPrimaryRole(user.primary_role || "staff");
  };

  const cancelEdit = () => {
    setEditingUser(null);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${editingUser}`, {
        body: {
          firstname: editFirstname,
          lastname: editLastname,
          email: editEmail,
          isadmin: editIsAdmin,
          ...editCapabilities,
          primary_role: editPrimaryRole,
        },
      });
      toast.success("User updated");
      cancelEdit();
      fetchUsers();
    } catch (error) {
      const errorMsg = error?.data?.error?.message || error?.message || "Failed to update user";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Quick toggle a capability without opening full edit
  const quickToggleCap = async (user, capKey) => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, {
        body: { [capKey]: !user[capKey] },
      });
      toast.success(`${capKey.replace('can_', '')} ${!user[capKey] ? 'enabled' : 'disabled'}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const toggleDeactivate = async (user) => {
    const action = user.deactivated_at ? "reactivate" : "deactivate";
    if (!(await confirm({
      message: `${action.charAt(0).toUpperCase() + action.slice(1)} ${user.firstname}?`,
      confirmLabel: action.charAt(0).toUpperCase() + action.slice(1),
    }))) return;

    setSaving(true);
    try {
      await api.post(`/users/${user.id}/${action}`);
      toast.success(`User ${action}d`);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to ${action} user`);
    } finally {
      setSaving(false);
    }
  };

  const removeFromAllEvents = async (user) => {
    const ok = await confirm({
      message:
        `Remove ${user.firstname} ${user.lastname} from ALL upcoming events?\n\n` +
        `This drops every crew assignment on events that haven't ended yet ` +
        `(past events stay as a record). Action cannot be undone.`,
      confirmLabel: "Remove from events",
      tone: "danger",
    });
    if (!ok) return;

    setSaving(true);
    try {
      const res = await api.post(`/users/${user.id}/remove-from-events`);
      if (res.removed === 0) {
        toast.success(`${user.firstname} had no upcoming assignments.`);
      } else {
        // Show up to 3 event names in the toast, "and N more" beyond that
        const names = (res.eventNames || []);
        const head = names.slice(0, 3).join(", ");
        const rest = names.length > 3 ? ` and ${names.length - 3} more` : "";
        toast.success(`Removed ${user.firstname} from ${res.removed} event${res.removed !== 1 ? "s" : ""}: ${head}${rest}`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to remove ${user.firstname} from events`);
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (filter === "active" && user.deactivated_at) return false;
    if (filter === "deactivated" && !user.deactivated_at) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = `${user.firstname || ""} ${user.lastname || ""}`.toLowerCase();
      return name.includes(q) || (user.email || "").toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return <LogoSpinner label="Loading users" />;

  return (
    <PageLayout kind="workspace" className="pt-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-primary">User Management</h1>
        <Appear show={printCart.length > 0}>
          <button
            onClick={() => setShowPrintCart(true)}
            className={buttonClass('tonal')}
          >
            Print Cart
            <span className="bg-primary text-on-primary px-2 py-0.5 rounded-full text-xs font-bold">
              {printCart.length}
            </span>
          </button>
        </Appear>
      </div>

      {/* Invitations. Above the roster because inviting someone is the reason to
          open this page for the first time. */}
      <UserInvites />

      {/* Search */}
      <SearchField
        label="Search users"
        placeholder="Search by name or email..."
        value={searchQuery}
        onChange={setSearchQuery}
        className="mb-4"
      />

      {/* Filter tabs */}
      <SegTrack
        label="Show users"
        className="mb-4"
        value={filter}
        onChange={setFilter}
        options={[
          { key: "active", label: "Active" },
          { key: "deactivated", label: "Deactivated" },
          { key: "all", label: "All" },
        ].map((o) => ({
          ...o,
          count: users.filter(u => o.key === "active" ? !u.deactivated_at : o.key === "deactivated" ? !!u.deactivated_at : true).length,
        }))}
      />

      {/* User list */}
      <div className="space-y-2">
        {filteredUsers.map((user) => (
          <Panel
            key={user.id}
            label={`${user.firstname || ""} ${user.lastname || ""}`.trim() || user.email}
            className={user.deactivated_at ? "opacity-60" : ""}
          >
            <FadeIn key={editingUser === user.id ? 'edit' : 'view'}>
            {editingUser === user.id ? (
              /* Edit mode */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="First Name">
                    <Field type="text" value={editFirstname} onChange={(e) => setEditFirstname(e.target.value)} />
                  </FieldRow>
                  <FieldRow label="Last Name">
                    <Field type="text" value={editLastname} onChange={(e) => setEditLastname(e.target.value)} />
                  </FieldRow>
                </div>
                <FieldRow label="Email">
                  <Field type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                </FieldRow>

                {/* Capabilities */}
                <div>
                  <label className="text-xs text-on-surface-variant mb-2 block">Capabilities</label>
                  <div className="flex flex-wrap gap-3">
                    {CAPABILITIES.map(cap => (
                      <Checkbox
                        key={cap.key}
                        checked={editCapabilities[cap.key] || false}
                        onChange={(e) => setEditCapabilities(prev => ({ ...prev, [cap.key]: e.target.checked }))}
                        label={<span className={`text-sm ${cap.color}`}>{cap.label}</span>}
                      />
                    ))}
                  </div>
                </div>

                <ConnectedBots userId={user.id} />

                {/* Primary Role */}
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Default Home Screen">
                    <Select
                      label="Default Home Screen"
                      value={editPrimaryRole}
                      onChange={setEditPrimaryRole}
                      options={ROLES.map(r => ({ key: r.value, label: r.label }))}
                    />
                  </FieldRow>
                  <div className="flex items-end">
                    <Checkbox
                      checked={editIsAdmin}
                      onChange={(e) => setEditIsAdmin(e.target.checked)}
                      label={<span className="text-white">Legacy Admin</span>}
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={saveEdit} disabled={saving}
                    className={buttonClass('filled')}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button onClick={cancelEdit}
                    className={buttonClass('quiet')}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* Display mode */
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-semibold text-white">
                        {user.firstname}{user.lastname ? ` ${user.lastname}` : ''}
                      </span>
                      <Appear show={!!user.deactivated_at}>
                        <Tag tone="bad">Deactivated</Tag>
                      </Appear>
                      {user.is_service && (
                        <Tag tone="info">Service</Tag>
                      )}
                    </div>
                    <div className="text-sm text-on-surface-variant">{user.email}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setIdCardUser(user)}
                      className={buttonClass('quiet', 'sm')}>
                      ID Card
                    </button>
                    <button onClick={() => startEdit(user)}
                      className={buttonClass('quiet', 'sm')}>
                      Edit
                    </button>
                    <Appear show={!user.is_service && !!user.deactivated_at}>
                      <Tooltip label="Drop this deactivated user from every upcoming event they're still crewed on (past events kept).">
                        <button onClick={() => removeFromAllEvents(user)} disabled={saving}
                          className={buttonClass('danger', 'sm')}>
                          Remove from Events
                        </button>
                      </Tooltip>
                    </Appear>
                    {!user.is_service && (
                      <button onClick={() => toggleDeactivate(user)} disabled={saving}
                        className={user.deactivated_at ? buttonClass('good', 'sm') : buttonClass('danger', 'sm')}>
                        {user.deactivated_at ? "Reactivate" : "Deactivate"}
                      </button>
                    )}
                  </div>
                </div>
                {/* Capability badges — clickable to quick-toggle */}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {CAPABILITIES.map(cap => (
                    <ChipToggle
                      key={cap.key}
                      size="sm"
                      checked={!!user[cap.key]}
                      onChange={() => quickToggleCap(user, cap.key)}
                      disabled={saving}
                    >
                      {cap.label}
                    </ChipToggle>
                  ))}
                  {user.primary_role && user.primary_role !== 'staff' && (
                    <Tag className="ml-1">Home: {user.primary_role}</Tag>
                  )}
                </div>
              </div>
            )}
            </FadeIn>
          </Panel>
        ))}
      </div>

      <Reveal show={filteredUsers.length === 0}>
        <EmptyState variant="area">No {filter === "all" ? "" : filter} users found.</EmptyState>
      </Reveal>

      {idCardUser && (
        <IDCardGenerator
          user={idCardUser}
          onClose={() => setIdCardUser(null)}
          cartCount={printCart.length}
          onAddToCart={(card) => setPrintCart((prev) => [...prev, card])}
        />
      )}

      {showPrintCart && (
        <IDCardPrintCart
          cards={printCart}
          onRemove={(index) => setPrintCart((prev) => prev.filter((_, i) => i !== index))}
          onClear={() => setPrintCart([])}
          onClose={() => setShowPrintCart(false)}
        />
      )}
    </PageLayout>
  );
};

export default UserAdmin;
